<!--
Copyright 2026 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license, please see LICENSE.md in the
project root for license information or contact permission@sei.cmu.edu for full terms.
-->

<div class="cssLayoutRowStartCenter">
  <div class="sp-icon">
    <mat-icon class="mdi-24px" fontIcon="mdi-certificate-outline" [ngStyle]="{'color': 'var(--mat-sys-primary)'}"></mat-icon>
  </div>
  <mat-form-field style="width: 300px; margin-left: 10px;">
    <input matInput [formControl]="filterControl" placeholder="Search" />
    @if (filterString) {
    <button mat-icon-button matSuffix (click)="filterControl.setValue('')" title="Clear Search">
      <mat-icon style="transform: scale(0.85)" fontIcon="mdi-close-circle-outline"></mat-icon>
    </button>
    }
  </mat-form-field>
  @if (canEdit) {
  <button mat-stroked-button (click)="importFramework()" [disabled]="importing" title="Import framework from JSON" style="margin-left: 10px;">
    <mat-icon fontIcon="mdi-upload"></mat-icon>
    @if (importing) { Importing... } @else { Import }
  </button>
  }
  @if (importError) {
  <span style="color: var(--mat-sys-error); margin-left: 10px; font-size: 13px;">{{ importError }}</span>
  }
  <div class="button-end">
    <mat-paginator #paginator [pageIndex]="0" [pageSize]="20"
      [pageSizeOptions]="[5, 10, 15, 20, 25, 50, 100, 200]"></mat-paginator>
  </div>
</div>

<section class="scrolling-region">
  <table mat-table #competencyFrameworkTable [dataSource]="competencyFrameworkDataSource" matSort (matSortChange)="sortChanged($event)" multiTemplateDataRows>
    <!-- action column -->
    <ng-container matColumnDef="action">
      <mat-header-cell *matHeaderCellDef class="column-action">
        @if (canEdit) {
        <button mat-icon-button (click)="addOrEditCompetencyFramework(null)" style="outline: none;"
          title="Add new competency framework">
          <mat-icon class="mdi-24px self-center" fontIcon="mdi-plus-circle"></mat-icon>
        </button>
        <button mat-icon-button title="Upload Competency Framework from JSON" style="outline: none;"
          [disabled]="importing"
          (click)="jsonInput.click(); $event.stopPropagation();">
          <mat-icon class="mdi-24px" fontIcon="mdi-upload"></mat-icon>
        </button>
        } &nbsp;
      </mat-header-cell>
      <mat-cell *matCellDef="let element" class="column-action">
        <div class="action-div">
          @if (canEdit) {
          <button mat-icon-button class="msel-action" (click)="addOrEditCompetencyFramework(element); $event.stopPropagation();"
            title="Edit {{ element.name }}">
            <mat-icon class="mdi-18px" fontIcon="mdi-square-edit-outline"></mat-icon>
          </button>
          <button mat-icon-button class="msel-action" (click)="downloadFramework(element); $event.stopPropagation();"
            title="Download {{ element.name }} as JSON">
            <mat-icon class="mdi-18px" fontIcon="mdi-download"></mat-icon>
          </button>
          <button mat-icon-button class="msel-action"
            (click)="deleteCompetencyFramework(element); $event.stopPropagation();"
            [disabled]="!canDeleteFramework(element.id)"
            [matTooltip]="getDeleteTooltip(element.id)"
            matTooltipPosition="above">
            <mat-icon class="mdi-18px" fontIcon="mdi-trash-can-outline"></mat-icon>
          </button>
          }
        </div>
      </mat-cell>
    </ng-container>
    <!-- name Column -->
    <ng-container matColumnDef="name">
      <mat-header-cell mat-sort-header="name" title="Name" *matHeaderCellDef class="column-name">
        Name
      </mat-header-cell>
      <mat-cell *matCellDef="let element" class="column-name">
        {{ element.name }}
      </mat-cell>
    </ng-container>
    <!-- version Column -->
    <ng-container matColumnDef="version">
      <mat-header-cell mat-sort-header="version" title="Version" *matHeaderCellDef class="column-version">
        Version
      </mat-header-cell>
      <mat-cell *matCellDef="let element" class="column-version">
        {{ element.version }}
      </mat-cell>
    </ng-container>
    <!-- source Column -->
    <ng-container matColumnDef="source">
      <mat-header-cell mat-sort-header="source" title="Source" *matHeaderCellDef class="column-source">
        Source
      </mat-header-cell>
      <mat-cell *matCellDef="let element" class="column-source">
        {{ element.source }}
      </mat-cell>
    </ng-container>
    <!-- scale Column -->
    <ng-container matColumnDef="scale">
      <mat-header-cell mat-sort-header="scale" title="Proficiency Scale" *matHeaderCellDef class="column-scale">
        Scale
      </mat-header-cell>
      <mat-cell *matCellDef="let element" class="column-scale">
        {{ getScaleName(element) }}
      </mat-cell>
    </ng-container>
    <!-- description Column -->
    <ng-container matColumnDef="description">
      <mat-header-cell mat-sort-header="description" title="Description" *matHeaderCellDef class="column-description">
        Description
      </mat-header-cell>
      <mat-cell *matCellDef="let element" class="column-description">
        {{ element.description }}
      </mat-cell>
    </ng-container>
    <!-- Expanded Content Column -->
    <ng-container matColumnDef="expandedDetail">
      <mat-cell class="expanded-detail-cell" *matCellDef="let element" [attr.colspan]="displayedColumns.length">
        <div [@detailExpand]="element.id == expandedElementId ? 'expanded' : 'collapsed'" style="overflow: hidden">
          @if (expandedElementId === element.id) {
          <!-- Work Roles -->
          @if (loadingCompetencies || workRoles.length > 0 || canEdit) {
          <mat-expansion-panel class="section-panel">
            <mat-expansion-panel-header>
              <mat-panel-title>
                <mat-icon class="mdi-18px panel-icon" fontIcon="mdi-account-hard-hat"></mat-icon>
                Work Roles
                @if (loadingCompetencies) {
                (Loading...)
                } @else {
                ({{ workRoles.length }})
                }
              </mat-panel-title>
            </mat-expansion-panel-header>
            <div class="element-toolbar" (click)="$event.stopPropagation();">
              <mat-form-field style="width: 250px; margin-left: 10px;">
                <input matInput [formControl]="workRoleFilterControl" placeholder="Search work roles" />
                @if (workRoleFilterString) {
                <button mat-icon-button matSuffix (click)="workRoleFilterControl.setValue('')" title="Clear Search">
                  <mat-icon style="transform: scale(0.85)" fontIcon="mdi-close-circle-outline"></mat-icon>
                </button>
                }
              </mat-form-field>
              @if (workRoleCategories.length > 0) {
              <mat-form-field style="width: 200px; margin-left: 10px;">
                <mat-label>Category</mat-label>
                <mat-select [value]="selectedWorkRoleCategory" (selectionChange)="onWorkRoleCategoryFilterChange($event.value)">
                  <mat-option value="">All</mat-option>
                  @for (cat of workRoleCategories; track cat) {
                  <mat-option [value]="cat">{{ cat }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              }
              <div class="button-end">
                <mat-paginator #workRolePaginator [pageIndex]="0" [pageSize]="10"
                  [pageSizeOptions]="[10, 20, 50, 100]"></mat-paginator>
              </div>
            </div>
            <div class="expanded-detail-div">
              <mat-table [dataSource]="workRoleDataSource" matSort (matSortChange)="workRoleSortChanged($event)" multiTemplateDataRows>
                <!-- action column -->
                <ng-container matColumnDef="action">
                  <mat-header-cell *matHeaderCellDef class="comp-col-action">
                    @if (canEdit) {
                    <button mat-icon-button (click)="addOrEditCompetency(null, 'Work Role'); $event.stopPropagation();" title="Add work role">
                      <mat-icon class="mdi-18px" fontIcon="mdi-plus-circle"></mat-icon>
                    </button>
                    }
                  </mat-header-cell>
                  <mat-cell *matCellDef="let c" class="comp-col-action">
                    @if (canEdit) {
                    <button mat-icon-button class="msel-action" (click)="addOrEditCompetency(c, 'Work Role'); $event.stopPropagation();" title="Edit {{ c.idNumber }}">
                      <mat-icon class="mdi-18px" fontIcon="mdi-square-edit-outline"></mat-icon>
                    </button>
                    <button mat-icon-button class="msel-action" (click)="deleteCompetency(c); $event.stopPropagation();" title="Delete {{ c.idNumber }}">
                      <mat-icon class="mdi-18px" fontIcon="mdi-trash-can-outline"></mat-icon>
                    </button>
                    }
                  </mat-cell>
                </ng-container>
                <!-- idNumber column -->
                <ng-container matColumnDef="idNumber">
                  <mat-header-cell mat-sort-header="idNumber" *matHeaderCellDef class="comp-col-id">ID</mat-header-cell>
                  <mat-cell *matCellDef="let c" class="comp-col-id">{{ c.idNumber }}</mat-cell>
                </ng-container>
                <!-- shortName column -->
                <ng-container matColumnDef="shortName">
                  <mat-header-cell mat-sort-header="shortName" *matHeaderCellDef class="comp-col-name">Name</mat-header-cell>
                  <mat-cell *matCellDef="let c" class="comp-col-name">{{ c.shortName }}</mat-cell>
                </ng-container>
                <!-- category column -->
                <ng-container matColumnDef="category">
                  <mat-header-cell mat-sort-header="category" *matHeaderCellDef class="comp-col-category">Category</mat-header-cell>
                  <mat-cell *matCellDef="let c" class="comp-col-category">{{ getWorkRoleCategory(c) }}</mat-cell>
                </ng-container>
                <!-- description column -->
                <ng-container matColumnDef="description">
                  <mat-header-cell mat-sort-header="description" *matHeaderCellDef class="comp-col-desc">Description</mat-header-cell>
                  <mat-cell *matCellDef="let c" class="comp-col-desc">{{ c.description }}</mat-cell>
                </ng-container>
                <!-- expanded related detail -->
                <ng-container matColumnDef="relatedDetail">
                  <mat-cell *matCellDef="let c" [attr.colspan]="workRoleDisplayedColumns.length" class="related-detail-cell">
                    @if (expandedCompetencyId === c.id) {
                    <div class="related-hint">Link competencies related to <strong>{{ c.idNumber || c.shortName }}</strong></div>
                    <div class="related-enchilada" (click)="$event.stopPropagation();">
                      <div class="related-list-container mat-elevation-z8">
                        <mat-toolbar class="related-toolbar">
                          <mat-toolbar-row >
                            <mat-icon class="mdi-18px header-icon" fontIcon="mdi-certificate-outline"></mat-icon>
                            <p matTooltip="Competencies in this framework that can be linked" matTooltipShowDelay="800">Available to Link</p>
                          </mat-toolbar-row>
                          <mat-toolbar-row >
                            <mat-form-field class="related-search">
                              <input matInput [formControl]="relatedFilterControl" placeholder="Search" autocomplete="off" />
                              @if (relatedFilterControl.value) {
                              <button mat-icon-button matSuffix (click)="relatedFilterControl.setValue('')" title="Clear">
                                <mat-icon style="transform: scale(0.85)" fontIcon="mdi-close-circle-outline"></mat-icon>
                              </button>
                              }
                            </mat-form-field>
                            @if (availableTypes.length > 1) {
                            <mat-form-field class="related-type-filter">
                              <mat-label>Type</mat-label>
                              <mat-select [value]="availableTypeFilter" (selectionChange)="onAvailableTypeFilterChange($event.value)">
                                <mat-option value="">All</mat-option>
                                @for (t of availableTypes; track t) {
                                <mat-option [value]="t">{{ t }}</mat-option>
                                }
                              </mat-select>
                            </mat-form-field>
                            }
                            <mat-paginator #availablePaginator [pageSize]="10" [pageSizeOptions]="[10, 25, 50, 100]"></mat-paginator>
                          </mat-toolbar-row>
                        </mat-toolbar>
                        <div class="related-table-scroll">
                          <mat-table [dataSource]="availableRelatedDataSource">
                            <ng-container matColumnDef="name">
                              <mat-cell *matCellDef="let r" [matTooltip]="r.description" matTooltipShowDelay="800">
                                <strong>{{ r.idNumber }}</strong>&nbsp;— {{ r.shortName }}
                              </mat-cell>
                            </ng-container>
                            <ng-container matColumnDef="view">
                              <mat-cell *matCellDef="let r" class="related-col-btn">
                                <button mat-icon-button (click)="viewCompetencyDetail(r)" title="View details">
                                  <mat-icon class="mdi-18px" fontIcon="mdi-magnify"></mat-icon>
                                </button>
                              </mat-cell>
                            </ng-container>
                            <ng-container matColumnDef="add">
                              <mat-cell *matCellDef="let r" class="related-col-btn">
                                <button mat-icon-button (click)="addRelatedCompetency(r)" title="Add {{ r.idNumber }}" [disabled]="!canEdit">
                                  <mat-icon class="mdi-18px" fontIcon="mdi-plus-circle-outline"></mat-icon>
                                </button>
                              </mat-cell>
                            </ng-container>
                            <mat-row *matRowDef="let row; columns: availableRelatedColumns"></mat-row>
                          </mat-table>
                          @if (availableRelatedDataSource.filteredData.length === 0) {
                          <div class="no-results-inline">No competencies available</div>
                          }
                        </div>
                      </div>
                      <div class="related-list-container mat-elevation-z8">
                        <mat-toolbar class="related-toolbar">
                          <mat-toolbar-row >
                            <mat-icon class="mdi-18px header-icon" fontIcon="mdi-link-variant"></mat-icon>
                            <p matTooltip="Parent, child, and cross-referenced competencies" matTooltipShowDelay="800">Related ({{ relatedDataSource.data.length }})</p>
                          </mat-toolbar-row>
                          <mat-toolbar-row >
                            <mat-form-field class="related-search">
                              <input matInput [formControl]="relatedSideFilterControl" placeholder="Search" autocomplete="off" />
                              @if (relatedSideFilterControl.value) {
                              <button mat-icon-button matSuffix (click)="relatedSideFilterControl.setValue('')" title="Clear">
                                <mat-icon style="transform: scale(0.85)" fontIcon="mdi-close-circle-outline"></mat-icon>
                              </button>
                              }
                            </mat-form-field>
                            @if (relatedTypes.length > 1) {
                            <mat-form-field class="related-type-filter">
                              <mat-label>Type</mat-label>
                              <mat-select [value]="relatedTypeFilter" (selectionChange)="onRelatedTypeFilterChange($event.value)">
                                <mat-option value="">All</mat-option>
                                @for (t of relatedTypes; track t) {
                                <mat-option [value]="t">{{ t }}</mat-option>
                                }
                              </mat-select>
                            </mat-form-field>
                            }
                            <mat-paginator #relatedPaginator [pageSize]="10" [pageSizeOptions]="[10, 25, 50, 100]"></mat-paginator>
                          </mat-toolbar-row>
                        </mat-toolbar>
                        <div class="related-table-scroll">
                          <mat-table [dataSource]="relatedDataSource">
                            <ng-container matColumnDef="name">
                              <mat-cell *matCellDef="let r" [matTooltip]="r.description" matTooltipShowDelay="800">
                                <strong>{{ r.idNumber }}</strong>&nbsp;— {{ r.shortName }}
                              </mat-cell>
                            </ng-container>
                            <ng-container matColumnDef="view">
                              <mat-cell *matCellDef="let r" class="related-col-btn">
                                <button mat-icon-button (click)="viewCompetencyDetail(r)" title="View details">
                                  <mat-icon class="mdi-18px" fontIcon="mdi-magnify"></mat-icon>
                                </button>
                              </mat-cell>
                            </ng-container>
                            <ng-container matColumnDef="remove">
                              <mat-cell *matCellDef="let r" class="related-col-btn">
                                <button mat-icon-button (click)="removeRelatedCompetency(r)" title="Remove {{ r.idNumber }}" [disabled]="!canEdit">
                                  <mat-icon class="mdi-18px" fontIcon="mdi-minus-circle-outline"></mat-icon>
                                </button>
                              </mat-cell>
                            </ng-container>
                            <mat-row *matRowDef="let row; columns: relatedColumns"></mat-row>
                          </mat-table>
                          @if (relatedDataSource.data.length === 0) {
                          <div class="no-results-inline">No related competencies</div>
                          }
                        </div>
                      </div>
                    </div>
                    }
                  </mat-cell>
                </ng-container>
                <!-- row definitions -->
                <mat-header-row *matHeaderRowDef="workRoleDisplayedColumns; sticky: true"></mat-header-row>
                <mat-row *matRowDef="let row; columns: workRoleDisplayedColumns;" class="comp-row"
                  [class.comp-row-expanded]="expandedCompetencyId === row.id"
                  (click)="toggleCompetencyExpand(row)"></mat-row>
                <mat-row *matRowDef="let row; columns: ['relatedDetail']" class="detail-row"></mat-row>
              </mat-table>
              @if (loadingCompetencies) {
                <div class="no-results-inline">Loading work roles...</div>
              } @else if (workRoleDataSource.filteredData.length === 0) {
                <div class="no-results-inline">No work roles found</div>
              }
            </div>
          </mat-expansion-panel>
          }
          <!-- Competencies -->
          <mat-expansion-panel class="section-panel">
            <mat-expansion-panel-header>
              <mat-panel-title>
                <mat-icon class="mdi-18px panel-icon" fontIcon="mdi-certificate-outline"></mat-icon>
                Competencies
                @if (loadingCompetencies) {
                (Loading...)
                } @else {
                ({{ expandedCompetencies.length }})
                }
              </mat-panel-title>
            </mat-expansion-panel-header>
            <div class="element-toolbar" (click)="$event.stopPropagation();">
              <mat-form-field style="width: 250px; margin-left: 10px;">
                <input matInput [formControl]="competencyFilterControl" placeholder="Search competencies" />
                @if (competencyFilterString) {
                <button mat-icon-button matSuffix (click)="competencyFilterControl.setValue('')" title="Clear Search">
                  <mat-icon style="transform: scale(0.85)" fontIcon="mdi-close-circle-outline"></mat-icon>
                </button>
                }
              </mat-form-field>
              @if (competencyTypes.length > 1) {
              <mat-form-field style="width: 180px; margin-left: 10px;">
                <mat-label>Type</mat-label>
                <mat-select [value]="selectedCompetencyType" (selectionChange)="onTypeFilterChange($event.value)">
                  <mat-option value="">All</mat-option>
                  @for (t of competencyTypes; track t) {
                  <mat-option [value]="t">{{ t }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              }
              <div class="button-end">
                <mat-paginator #competencyPaginator [pageIndex]="0" [pageSize]="10"
                  [pageSizeOptions]="[10, 20, 50, 100, 200]"></mat-paginator>
              </div>
            </div>
            <div class="expanded-detail-div">
              <mat-table [dataSource]="competencyDataSource" matSort #competencyTableSort="matSort" (matSortChange)="competencySortChanged($event)" multiTemplateDataRows>
                <!-- action column -->
                <ng-container matColumnDef="action">
                  <mat-header-cell *matHeaderCellDef class="comp-col-action">
                    @if (canEdit) {
                    <button mat-icon-button (click)="addOrEditCompetency(null); $event.stopPropagation();" title="Add competency">
                      <mat-icon class="mdi-18px" fontIcon="mdi-plus-circle"></mat-icon>
                    </button>
                    }
                  </mat-header-cell>
                  <mat-cell *matCellDef="let c" class="comp-col-action">
                    @if (canEdit) {
                    <button mat-icon-button class="msel-action" (click)="addOrEditCompetency(c); $event.stopPropagation();" title="Edit {{ c.idNumber }}">
                      <mat-icon class="mdi-18px" fontIcon="mdi-square-edit-outline"></mat-icon>
                    </button>
                    <button mat-icon-button class="msel-action" (click)="deleteCompetency(c); $event.stopPropagation();" title="Delete {{ c.idNumber }}">
                      <mat-icon class="mdi-18px" fontIcon="mdi-trash-can-outline"></mat-icon>
                    </button>
                    }
                  </mat-cell>
                </ng-container>
                <!-- idNumber column -->
                <ng-container matColumnDef="idNumber">
                  <mat-header-cell mat-sort-header="idNumber" *matHeaderCellDef class="comp-col-id">ID</mat-header-cell>
                  <mat-cell *matCellDef="let c" class="comp-col-id">{{ c.idNumber }}</mat-cell>
                </ng-container>
                <!-- type column -->
                <ng-container matColumnDef="type">
                  <mat-header-cell mat-sort-header="type" *matHeaderCellDef class="comp-col-type">Type</mat-header-cell>
                  <mat-cell *matCellDef="let c" class="comp-col-type">{{ getCompetencyType(c) }}</mat-cell>
                </ng-container>
                <!-- shortName column -->
                <ng-container matColumnDef="shortName">
                  <mat-header-cell mat-sort-header="shortName" *matHeaderCellDef class="comp-col-name">Short Name</mat-header-cell>
                  <mat-cell *matCellDef="let c" class="comp-col-name">{{ c.shortName }}</mat-cell>
                </ng-container>
                <!-- description column -->
                <ng-container matColumnDef="description">
                  <mat-header-cell mat-sort-header="description" *matHeaderCellDef class="comp-col-desc">Description</mat-header-cell>
                  <mat-cell *matCellDef="let c" class="comp-col-desc">{{ c.description }}</mat-cell>
                </ng-container>
                <!-- expanded related detail -->
                <ng-container matColumnDef="relatedDetail">
                  <mat-cell *matCellDef="let c" [attr.colspan]="competencyDisplayedColumns.length" class="related-detail-cell">
                    @if (expandedCompetencyId === c.id) {
                    <div class="related-hint">Link competencies related to <strong>{{ c.idNumber || c.shortName }}</strong></div>
                    <div class="related-enchilada" (click)="$event.stopPropagation();">
                      <div class="related-list-container mat-elevation-z8">
                        <mat-toolbar class="related-toolbar">
                          <mat-toolbar-row >
                            <mat-icon class="mdi-18px header-icon" fontIcon="mdi-certificate-outline"></mat-icon>
                            <p matTooltip="Competencies in this framework that can be linked" matTooltipShowDelay="800">Available to Link</p>
                          </mat-toolbar-row>
                          <mat-toolbar-row >
                            <mat-form-field class="related-search">
                              <input matInput [formControl]="relatedFilterControl" placeholder="Search" autocomplete="off" />
                              @if (relatedFilterControl.value) {
                              <button mat-icon-button matSuffix (click)="relatedFilterControl.setValue('')" title="Clear">
                                <mat-icon style="transform: scale(0.85)" fontIcon="mdi-close-circle-outline"></mat-icon>
                              </button>
                              }
                            </mat-form-field>
                            @if (availableTypes.length > 1) {
                            <mat-form-field class="related-type-filter">
                              <mat-label>Type</mat-label>
                              <mat-select [value]="availableTypeFilter" (selectionChange)="onAvailableTypeFilterChange($event.value)">
                                <mat-option value="">All</mat-option>
                                @for (t of availableTypes; track t) {
                                <mat-option [value]="t">{{ t }}</mat-option>
                                }
                              </mat-select>
                            </mat-form-field>
                            }
                            <mat-paginator #availablePaginator [pageSize]="10" [pageSizeOptions]="[10, 25, 50, 100]"></mat-paginator>
                          </mat-toolbar-row>
                        </mat-toolbar>
                        <div class="related-table-scroll">
                          <mat-table [dataSource]="availableRelatedDataSource">
                            <ng-container matColumnDef="name">
                              <mat-cell *matCellDef="let r" [matTooltip]="r.description" matTooltipShowDelay="800">
                                <strong>{{ r.idNumber }}</strong>&nbsp;— {{ r.shortName }}
                              </mat-cell>
                            </ng-container>
                            <ng-container matColumnDef="view">
                              <mat-cell *matCellDef="let r" class="related-col-btn">
                                <button mat-icon-button (click)="viewCompetencyDetail(r)" title="View details">
                                  <mat-icon class="mdi-18px" fontIcon="mdi-magnify"></mat-icon>
                                </button>
                              </mat-cell>
                            </ng-container>
                            <ng-container matColumnDef="add">
                              <mat-cell *matCellDef="let r" class="related-col-btn">
                                <button mat-icon-button (click)="addRelatedCompetency(r)" title="Add {{ r.idNumber }}" [disabled]="!canEdit">
                                  <mat-icon class="mdi-18px" fontIcon="mdi-plus-circle-outline"></mat-icon>
                                </button>
                              </mat-cell>
                            </ng-container>
                            <mat-row *matRowDef="let row; columns: availableRelatedColumns"></mat-row>
                          </mat-table>
                          @if (availableRelatedDataSource.filteredData.length === 0) {
                          <div class="no-results-inline">No competencies available</div>
                          }
                        </div>
                      </div>
                      <div class="related-list-container mat-elevation-z8">
                        <mat-toolbar class="related-toolbar">
                          <mat-toolbar-row >
                            <mat-icon class="mdi-18px header-icon" fontIcon="mdi-link-variant"></mat-icon>
                            <p matTooltip="Parent, child, and cross-referenced competencies" matTooltipShowDelay="800">Related ({{ relatedDataSource.data.length }})</p>
                          </mat-toolbar-row>
                          <mat-toolbar-row >
                            <mat-form-field class="related-search">
                              <input matInput [formControl]="relatedSideFilterControl" placeholder="Search" autocomplete="off" />
                              @if (relatedSideFilterControl.value) {
                              <button mat-icon-button matSuffix (click)="relatedSideFilterControl.setValue('')" title="Clear">
                                <mat-icon style="transform: scale(0.85)" fontIcon="mdi-close-circle-outline"></mat-icon>
                              </button>
                              }
                            </mat-form-field>
                            @if (relatedTypes.length > 1) {
                            <mat-form-field class="related-type-filter">
                              <mat-label>Type</mat-label>
                              <mat-select [value]="relatedTypeFilter" (selectionChange)="onRelatedTypeFilterChange($event.value)">
                                <mat-option value="">All</mat-option>
                                @for (t of relatedTypes; track t) {
                                <mat-option [value]="t">{{ t }}</mat-option>
                                }
                              </mat-select>
                            </mat-form-field>
                            }
                            <mat-paginator #relatedPaginator [pageSize]="10" [pageSizeOptions]="[10, 25, 50, 100]"></mat-paginator>
                          </mat-toolbar-row>
                        </mat-toolbar>
                        <div class="related-table-scroll">
                          <mat-table [dataSource]="relatedDataSource">
                            <ng-container matColumnDef="name">
                              <mat-cell *matCellDef="let r" [matTooltip]="r.description" matTooltipShowDelay="800">
                                <strong>{{ r.idNumber }}</strong>&nbsp;— {{ r.shortName }}
                              </mat-cell>
                            </ng-container>
                            <ng-container matColumnDef="view">
                              <mat-cell *matCellDef="let r" class="related-col-btn">
                                <button mat-icon-button (click)="viewCompetencyDetail(r)" title="View details">
                                  <mat-icon class="mdi-18px" fontIcon="mdi-magnify"></mat-icon>
                                </button>
                              </mat-cell>
                            </ng-container>
                            <ng-container matColumnDef="remove">
                              <mat-cell *matCellDef="let r" class="related-col-btn">
                                <button mat-icon-button (click)="removeRelatedCompetency(r)" title="Remove {{ r.idNumber }}" [disabled]="!canEdit">
                                  <mat-icon class="mdi-18px" fontIcon="mdi-minus-circle-outline"></mat-icon>
                                </button>
                              </mat-cell>
                            </ng-container>
                            <mat-row *matRowDef="let row; columns: relatedColumns"></mat-row>
                          </mat-table>
                          @if (relatedDataSource.data.length === 0) {
                          <div class="no-results-inline">No related competencies</div>
                          }
                        </div>
                      </div>
                    </div>
                    }
                  </mat-cell>
                </ng-container>
                <!-- row definitions -->
                <mat-header-row *matHeaderRowDef="competencyDisplayedColumns; sticky: true"></mat-header-row>
                <mat-row *matRowDef="let row; columns: competencyDisplayedColumns;" class="comp-row"
                  [class.comp-row-expanded]="expandedCompetencyId === row.id"
                  (click)="toggleCompetencyExpand(row)"></mat-row>
                <mat-row *matRowDef="let row; columns: ['relatedDetail']" class="detail-row"></mat-row>
              </mat-table>
              @if (loadingCompetencies) {
                <div class="no-results-inline">Loading competencies...</div>
              } @else if (competencyDataSource.filteredData.length === 0) {
                <div class="no-results-inline">No competencies found</div>
              }
            </div>
          </mat-expansion-panel>
          }
        </div>
      </mat-cell>
    </ng-container>
    <!-- row definitions -->
    <mat-header-row *matHeaderRowDef="displayedColumns"></mat-header-row>
    <mat-row *matRowDef="let row; columns: displayedColumns;" matRipple [class]="getRowClass(row.id)"
      (click)="rowClicked(row)">
    </mat-row>
    <mat-row *matRowDef="let row; columns: ['expandedDetail']" class="detail-row">
    </mat-row>
  </table>
  @if (competencyFrameworkDataSource?.filteredData.length === 0) {
  <div class="no-results">No Competency Frameworks found</div>
  }
</section>

<input hidden type="file" #jsonInput accept=".json" (change)="selectJsonFile($event)" />
